import { useQueryClient } from "@tanstack/react-query";
import { CreditFacilityEventLayout } from "features/credit-facility/CreditFacilityEventLayout";
import { CreditFacilitySubpageProps } from "features/credit-facility/CreditFacilitySubpageProps";
import { ReallocationTable } from "features/credit-facility/reallocation/ReallocationTable";
import {
  getCreditFacilityReallocationDataRetrieveQueryKey,
  getCreditFacilityReallocationSummaryRetrieveQueryKey,
  useCreditFacilityReallocationCreate,
  useCreditFacilityReallocationDataRetrieveSuspense,
  useCreditFacilityRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { CreditFacilityPrincipalUseReallocationDataRequest } from "gen/model/creditFacilityPrincipalUseReallocationDataRequest";
import { CreditFacilityPrincipalUseReallocationDataRequestUsesItem } from "gen/model/creditFacilityPrincipalUseReallocationDataRequestUsesItem";
import { CreditFacilityPrincipalUseReallocationUpsertData } from "gen/model/creditFacilityPrincipalUseReallocationUpsertData";
import { CreditFacilityReallocationExistingUseData } from "gen/model/creditFacilityReallocationExistingUseData";
import { EntityFunctionalTypeEnum } from "gen/model/entityFunctionalTypeEnum";
import { FundEntityData } from "gen/model/fundEntityData";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { ContentBlockGroup } from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { useQueryParams } from "shared/hooks/useQueryParams";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const getDefaultNewUse = (
  funds: FundEntityData[],
  existingUse: CreditFacilityReallocationExistingUseData,
): CreditFacilityPrincipalUseReallocationUpsertData => ({
  fund_amounts: funds.reduce<
    Record<
      string,
      {
        amount: string;
        fund_id: string;
      }
    >
  >(
    (reducer, fund) => ({
      ...reducer,
      [fund.id!]: {
        amount: "0.00",
        fund_id: fund.id!,
      },
    }),
    {},
  ),
  is_prorata: true,
  transaction_code_id: "",
  amount: "0.00",
  use_type: "" as never,
  principal_tranche_id: existingUse.principal_tranche_id,
  fund_family_amount: "0.00",
});

export const CreditFacilityReallocationSplit: React.FC = () => {
  const { ids: queryIds } = useQueryParams();
  const { navigateNext } = usePageWizard();
  const queryClient = useQueryClient();
  const fund_family_principal_use_ids = decodeURIComponent(queryIds);
  const { fundFamilyId, creditFacilityId, fundFamilyEvent } =
    useOutletContext<CreditFacilitySubpageProps>();

  const { data: creditFacility } = useCreditFacilityRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { data: funds } = useFundListSuspense({
    fund_ids: creditFacility.borrowing_entity_ids ?? [],
    extra_functional_types: [EntityFunctionalTypeEnum.holding_company],
  });

  const { data } = useCreditFacilityReallocationDataRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    fundFamilyEvent.id!,
    {
      fund_family_principal_use_ids: fund_family_principal_use_ids
        ? fund_family_principal_use_ids.split(",")
        : [],
    },
  );
  const upsertData: CreditFacilityPrincipalUseReallocationDataRequest = {
    ...data,
    uses: data.uses.map((useGroup) =>
      Object.entries(
        useGroup,
      ).reduce<CreditFacilityPrincipalUseReallocationDataRequestUsesItem>(
        (existingUses, [id, existingUse]) => ({
          ...existingUses,
          [id]: existingUse.new_uses.length
            ? existingUse
            : {
                ...existingUse,
                new_uses: [getDefaultNewUse(funds, existingUse)],
              },
        }),
        {},
      ),
    ),
  };
  const { mutateAsync: createReallocations } =
    useCreditFacilityReallocationCreate({
      mutation: {
        onSuccess: async () => {
          await invalidateQueryKeys(
            queryClient,
            getCreditFacilityReallocationDataRetrieveQueryKey(
              fundFamilyId,
              creditFacilityId,
              fundFamilyEvent.id!,
            ),
            getCreditFacilityReallocationSummaryRetrieveQueryKey(
              fundFamilyId,
              creditFacilityId,
              fundFamilyEvent.id!,
            ),
          );
        },
      },
    });

  const onSubmit = useCallback(
    async (data: CreditFacilityPrincipalUseReallocationDataRequest) => {
      await createReallocations({
        fundFamilyId,
        creditFacilityId,
        fundFamilyEventId: fundFamilyEvent.id!,
        data,
      });
    },
    [fundFamilyId, creditFacilityId, fundFamilyEvent, createReallocations],
  );

  const form = useForm<CreditFacilityPrincipalUseReallocationDataRequest>({
    values: upsertData,
  });
  return (
    <CreditFacilityEventLayout canEditEvent={true}>
      <Form form={form} onSubmit={onSubmit} onSuccess={navigateNext}>
        <ContentBlockGroup>
          {data.uses.map((group, index) => (
            <ReallocationTable
              key={index}
              group={{ group, index }}
              funds={funds}
            />
          ))}
        </ContentBlockGroup>
        <PageWizardFormFooter />
      </Form>
    </CreditFacilityEventLayout>
  );
};
